/** Shared API response shapes for the control-plane SPA. */

export type UserRole = "owner" | "admin" | "user";

export type UserProfile = {
  userId: string;
  email: string;
  role: string;
  status: string;
  /** Optional display name (editable in profile). */
  displayName?: string;
};

/** True when role may call /api/v1/admin/* (owner or admin). */
export function isAdminRole(role: string | undefined | null): boolean {
  return role === "owner" || role === "admin";
}

/** Own-user audit event from GET /api/v1/audit (and admin user audit). */
export type AuditEvent = {
  id: string;
  at: string;
  action: string;
  accountId?: string;
  /** Human label for accountId when resolvable from ACCT#. */
  accountLabel?: string;
  tool?: string;
  meta?: Record<string, unknown>;
};

export type FlagStatus = "open" | "acked";
export type FlagSeverity = "low" | "medium" | "high";

/** Admin anomaly flag from GET /api/v1/admin/flags. */
export type AdminFlag = {
  flagId: string;
  severity: FlagSeverity | string;
  reason: string;
  createdAt: string;
  status: FlagStatus | string;
  userId?: string;
  ackedAt?: string;
  ackedBy?: string;
  meta?: Record<string, unknown>;
};

/** Invite status from GET /api/v1/admin/invites. */
export type InviteStatus = "pending" | "accepted" | "revoked" | "expired" | string;

/** Admin invite row (never includes raw token). */
export type AdminInvite = {
  id: string;
  email: string;
  role: UserRole | string;
  status: InviteStatus;
  invitedBy: string;
  createdAt: string;
  expiresAt: string;
  resendCount: number;
  acceptedAt?: string;
  acceptedUserId?: string;
};

/** Create/resend response includes one-time inviteUrl + delivery status. */
export type AdminInviteCreated = AdminInvite & {
  inviteUrl: string;
  emailDelivery?: {
    ses?: "sent" | "failed" | "skipped" | string;
    cognito?: "sent" | "failed" | string;
  };
};

/** Admin user pointer from GET /api/v1/admin/users. */
export type AdminUser = {
  userId: string;
  email: string;
  role: UserRole | string;
  status: string;
  createdAt: string;
};

export type AuthChallenge = {
  name: string;
  session: string;
  parameters?: Record<string, string>;
};

export type LoginSuccess = {
  user: UserProfile;
};

export type LoginChallengeResponse = {
  challenge: AuthChallenge;
};

export type LoginResponse = LoginSuccess | LoginChallengeResponse;

/** PAT / MCP tool scopes (Phase B). */
export type PatScope = "read" | "send" | "full";

export const PAT_SCOPE_VALUES: readonly PatScope[] = [
  "read",
  "send",
  "full",
] as const;

export type PatMeta = {
  id: string;
  name: string;
  tokenPrefix: string;
  createdAt: string;
  expiresAt: string;
  lastUsedAt?: string;
  /**
   * Effective scopes from list/mint.
   * Missing on older servers → treat as `['full']` (Phase A tokens).
   */
  scopes?: PatScope[];
};

export type MintedPat = PatMeta & {
  token: string;
};

/** MCP client connection from GET /api/v1/mcp-clients */
export type McpClient = {
  id: string;
  kind: "pat" | "oauth";
  name: string;
  status: "active" | "revoked" | string;
  createdAt: string;
  lastUsedAt: string;
  scopes?: PatScope[];
  product?: string;
  patTokenId?: string;
};

/**
 * Public feature flags from GET /api/v1/features.
 * Keys match server `FeatureFlags` (dotted names).
 */
export type FeatureFlags = {
  "pats.scopesWrite": boolean;
  "pats.scopesEnforce": boolean;
  "invites.productized": boolean;
  "auth.selfSignup": boolean;
  "attachments.presign": boolean;
  "edge.cloudfront": boolean;
  "docs.portal": boolean;
  "abuse.anomalyFlags": boolean;
};

/** Client defaults when features endpoint is unavailable (pre-B0 or offline). */
export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  "pats.scopesWrite": true,
  "pats.scopesEnforce": false,
  "invites.productized": false,
  "auth.selfSignup": true,
  "attachments.presign": false,
  "edge.cloudfront": false,
  "docs.portal": false,
  "abuse.anomalyFlags": false,
};

export function isPatScope(value: unknown): value is PatScope {
  return value === "read" || value === "send" || value === "full";
}

/** Normalize list/mint scopes for UI (missing → full). */
export function scopesOf(pat: { scopes?: unknown }): PatScope[] {
  if (!Array.isArray(pat.scopes) || pat.scopes.length === 0) {
    return ["full"];
  }
  const out: PatScope[] = [];
  for (const s of pat.scopes) {
    if (!isPatScope(s)) continue;
    if (s === "full") return ["full"];
    if (!out.includes(s)) out.push(s);
  }
  return out.length > 0 ? out : ["full"];
}

export type ApiErrorBody = {
  error?: string;
  error_description?: string;
};

/** Pre-login invite preview (GET /api/v1/invites/preview). */
export type InvitePreview = {
  email: string;
  status: string;
  role?: string;
  expiresAt?: string;
  inviteId?: string;
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly body: unknown;

  constructor(status: number, code: string, message: string, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

export function isLoginChallenge(
  r: LoginResponse,
): r is LoginChallengeResponse {
  return "challenge" in r && r.challenge != null;
}
